import { Component, Input } from '@angular/core';

@Component({
    selector: 'app-skeleton-table',
    template: `
        <div class="skeleton-table-wrapper">
            <!-- Table Action Toolbar Shimmer -->
            <div class="sk-toolbar" *ngIf="showToolbar">
                <div class="sk-search-box skeleton-shimmer"></div>
                <div class="sk-actions">
                    <div class="sk-btn-filter skeleton-shimmer"></div>
                    <div class="sk-btn-add skeleton-shimmer"></div>
                </div>
            </div>

            <!-- Table Card Container -->
            <div class="sk-table-card">
                <table class="sk-table">
                    <!-- Table Header -->
                    <thead *ngIf="showHeaders">
                        <tr>
                            <th *ngFor="let col of columnList" [style.width]="col.width">
                                <div class="sk-header-cell skeleton-shimmer" [style.width]="col.headerWidth"></div>
                            </th>
                        </tr>
                    </thead>
                    <!-- Table Body -->
                    <tbody>
                        <tr *ngFor="let row of rowList">
                            <td *ngFor="let col of columnList" [style.width]="col.width">
                                <div class="sk-cell-content" [ngClass]="col.alignClass">
                                    <div *ngIf="col.type === 'avatar'" class="sk-avatar-group">
                                        <div class="sk-avatar-circle skeleton-shimmer"></div>
                                        <div class="sk-text-stack">
                                            <div class="sk-line-primary skeleton-shimmer"></div>
                                            <div class="sk-line-secondary skeleton-shimmer"></div>
                                        </div>
                                    </div>

                                    <div *ngIf="col.type === 'badge'" class="sk-badge skeleton-shimmer"></div>

                                    <div *ngIf="col.type === 'actions'" class="sk-action-buttons">
                                        <div class="sk-action-btn skeleton-shimmer"></div>
                                        <div class="sk-action-btn skeleton-shimmer"></div>
                                    </div>

                                    <div *ngIf="!col.type || col.type === 'text'" class="sk-line-text skeleton-shimmer" [style.width]="col.textWidth"></div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Pagination Footer Shimmer -->
                <div class="sk-pagination" *ngIf="showPagination">
                    <div class="sk-page-info skeleton-shimmer"></div>
                    <div class="sk-page-buttons">
                        <div class="sk-page-btn skeleton-shimmer"></div>
                        <div class="sk-page-btn skeleton-shimmer"></div>
                        <div class="sk-page-btn skeleton-shimmer"></div>
                    </div>
                </div>
            </div>
        </div>
    `,
    styles: [`
        .skeleton-table-wrapper {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            width: 100%;
        }

        .sk-toolbar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 1rem;
            flex-wrap: wrap;

            .sk-search-box {
                width: 280px;
                height: 38px;
                border-radius: 8px;
            }

            .sk-actions {
                display: flex;
                gap: 0.5rem;

                .sk-btn-filter {
                    width: 90px;
                    height: 38px;
                    border-radius: 8px;
                }

                .sk-btn-add {
                    width: 130px;
                    height: 38px;
                    border-radius: 8px;
                }
            }
        }

        .sk-table-card {
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

            :host-context(.dark) & {
                background: #0f172a;
                border-color: #334155;
            }
        }

        .sk-table {
            width: 100%;
            border-collapse: collapse;

            th {
                padding: 1rem;
                background: #f8fafc;
                border-bottom: 1px solid #e2e8f0;
                text-align: left;

                :host-context(.dark) & {
                    background: #111c38;
                    border-color: #1e2d52;
                }
            }

            td {
                padding: 1rem;
                border-bottom: 1px solid #f1f5f9;
                vertical-align: middle;

                :host-context(.dark) & {
                    border-color: #1e293b;
                }
            }

            tr:last-child td {
                border-bottom: none;
            }
        }

        .sk-header-cell {
            height: 14px;
            border-radius: 4px;
        }

        .sk-avatar-group {
            display: flex;
            align-items: center;
            gap: 0.75rem;

            .sk-avatar-circle {
                width: 38px;
                height: 38px;
                border-radius: 50%;
                flex-shrink: 0;
            }

            .sk-text-stack {
                display: flex;
                flex-direction: column;
                gap: 0.35rem;
                width: 140px;

                .sk-line-primary {
                    height: 12px;
                    width: 100%;
                    border-radius: 4px;
                }

                .sk-line-secondary {
                    height: 9px;
                    width: 70%;
                    border-radius: 4px;
                }
            }
        }

        .sk-badge {
            width: 75px;
            height: 24px;
            border-radius: 6px;
        }

        .sk-action-buttons {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 0.4rem;

            .sk-action-btn {
                width: 32px;
                height: 32px;
                border-radius: 8px;
            }
        }

        .sk-line-text {
            height: 12px;
            border-radius: 4px;
        }

        .sk-pagination {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.875rem 1.25rem;
            background: #f8fafc;
            border-top: 1px solid #e2e8f0;

            .sk-page-info {
                width: 150px;
                height: 14px;
                border-radius: 4px;
            }

            .sk-page-buttons {
                display: flex;
                gap: 0.35rem;

                .sk-page-btn {
                    width: 30px;
                    height: 30px;
                    border-radius: 6px;
                }
            }

            :host-context(.dark) & {
                background: #111c38;
                border-color: #1e2d52;
            }
        }
    `]
})
export class SkeletonTableComponent {
    @Input() rows: number = 5;
    @Input() columns: number = 5;
    @Input() showToolbar: boolean = true;
    @Input() showHeaders: boolean = true;
    @Input() showPagination: boolean = true;

    get rowList(): number[] {
        return Array.from({ length: this.rows }, (_, i) => i);
    }

    get columnList(): Array<{ width?: string; headerWidth: string; textWidth: string; type?: string; alignClass?: string }> {
        const defaultConfigs = [
            { width: '28%', headerWidth: '100px', textWidth: '100%', type: 'avatar' },
            { width: '20%', headerWidth: '80px', textWidth: '85%', type: 'text' },
            { width: '18%', headerWidth: '70px', textWidth: '75%', type: 'text' },
            { width: '16%', headerWidth: '60px', textWidth: '80px', type: 'badge' },
            { width: '18%', headerWidth: '50px', textWidth: '70px', type: 'actions', alignClass: 'text-right' },
        ];

        if (this.columns <= 5) {
            return defaultConfigs.slice(0, this.columns);
        }

        const result = [...defaultConfigs];
        for (let i = 5; i < this.columns; i++) {
            result.splice(result.length - 1, 0, {
                width: `${Math.round(100 / this.columns)}%`,
                headerWidth: '70px',
                textWidth: '80%',
                type: 'text'
            });
        }
        return result;
    }
}
